/**
 * @module AlpineComponents
 * @description Central registry and initialization for Alpine.js
 *
 * This file imports, registers, and initializes all Alpine.js components.
 */

import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import focus from '@alpinejs/focus';
import intersect from '@alpinejs/intersect';
import AlgoliaAccessories from './search/products/AlgoliaAccessories.js';
import AlgoliaAllProducts from './search/products/AlgoliaAllProducts.js';
import AlgoliaCncs from './search/products/AlgoliaCncs.js';
import AlgoliaRobocuts from './search/products/AlgoliaRobocuts.js';
import AlgoliaRobodrills from './search/products/AlgoliaRobodrills.js';
import AlgoliaRobotAndGo from './search/products/AlgoliaRobotAndGo.js';
import AlgoliaRobots from './search/products/AlgoliaRobots.js';
import AlgoliaRoboshots from './search/products/AlgoliaRoboshots.js';
import AlgoliaSoftware from './search/products/AlgoliaSoftware.js';
import EspFinder from './finders/EspFinder.js';
import FormieEnhance from './forms/FormieEnhance.js';
import GlobalSearch from './search/GlobalSearch.js';
import GlobalSearchModal from './search/GlobalSearchModal.js';
import ImageGalleryZoom from './media/ImageGalleryZoom.js';
import KeyboardShortcuts from './ui/KeyboardShortcuts.js';
import MegaMenu from './navigation/MegaMenu.js';
import Modal from './ui/Modal.js';
import NewsletterModal from './ui/NewsletterModal.js';
import SolutionFinder from './finders/SolutionFinder.js';
import SpotlightCarousel from './ui/SpotlightCarousel.js';
// Old sticky nav replaced by StickyNavDropdown — safe to delete StickyNav.js
// import StickyNav from './navigation/StickyNav.js';
import StickyNavDropdown from './navigation/StickyNavDropdown.js';
import WistiaVideo from './media/WistiaVideo.js';

Alpine.plugin(collapse);
Alpine.plugin(focus);
Alpine.plugin(intersect);

// Make Alpine available globally (required for plugins like breakout-grid-visualizer)
window.Alpine = Alpine;

// Register all Alpine.js components
Alpine.data('algoliaAccessories', AlgoliaAccessories);
Alpine.data('algoliaAllProducts', AlgoliaAllProducts);
Alpine.data('algoliaCncs', AlgoliaCncs);
Alpine.data('algoliaRobocuts', AlgoliaRobocuts);
Alpine.data('algoliaRobodrills', AlgoliaRobodrills);
Alpine.data('algoliaRobotAndGo', AlgoliaRobotAndGo);
Alpine.data('algoliaRobots', AlgoliaRobots);
Alpine.data('algoliaRoboshots', AlgoliaRoboshots);
Alpine.data('algoliaSoftware', AlgoliaSoftware);
Alpine.data('espFinder', EspFinder);
Alpine.data('formieEnhance', FormieEnhance);
Alpine.data('globalSearch', GlobalSearch);
Alpine.data('globalSearchModal', GlobalSearchModal);
Alpine.data('imageGalleryZoom', ImageGalleryZoom);
Alpine.data('keyboardShortcuts', KeyboardShortcuts);
Alpine.data('megaMenu', MegaMenu);
Alpine.data('modal', Modal);
Alpine.data('newsletterModal', NewsletterModal);
Alpine.data('solutionFinder', SolutionFinder);
Alpine.data('spotlightCarousel', SpotlightCarousel);
// Alpine.data('stickyNav', StickyNav);
Alpine.data('stickyNavDropdown', StickyNavDropdown);
Alpine.data('wistiaVideo', WistiaVideo);

// Initialize Alpine (only once)
async function initAlpine() {
  // Prevent double initialization during HMR
  if (window.__alpineStarted) return;

  // Conditionally load visualizer only in development
  if (import.meta.env.DEV) {
    try {
      await import('@astuteo/breakout-grid/visualizer');
    } catch (e) {
      console.warn(
        '[Alpine] Failed to load breakout-grid-visualizer:',
        e.message,
      );
    }
  }

  Alpine.start();
  window.__alpineStarted = true;
}

initAlpine();

export {
  Alpine,
  AlgoliaAccessories,
  AlgoliaAllProducts,
  AlgoliaCncs,
  AlgoliaRobocuts,
  AlgoliaRobodrills,
  AlgoliaRobotAndGo,
  AlgoliaRobots,
  AlgoliaRoboshots,
  AlgoliaSoftware,
  EspFinder,
  FormieEnhance,
  GlobalSearch,
  GlobalSearchModal,
  ImageGalleryZoom,
  KeyboardShortcuts,
  MegaMenu,
  Modal,
  NewsletterModal,
  SolutionFinder,
  SpotlightCarousel,
  // StickyNav,
  StickyNavDropdown,
  WistiaVideo,
};
